import { Directive, inject, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, map, mergeMap, Observable, of, Subscription, take } from 'rxjs';
import { FeatureFlagKeys, FeatureFlagService } from 'src/app/core/services/feature-flag/feature-flag.service';

/**
 * Directive to conditionally display a template based on a feature flag.
 *
 * This directive uses the `FeatureFlagService` to determine whether a specific feature
 * flag is enabled or disabled. It supports an optional "else" template to display
 * when the feature flag is not enabled.
 *
 * Usage:
 * - Apply the directive to an element with the `opFF` input to specify the feature flag key.
 * - Optionally, use the `opFFElse` input to provide an alternate template to display when the feature flag is disabled.
 *
 * Example:
 * ```html
 * <div *opFF="'featureKey'; else otherTemplate">
 *   Feature is enabled!
 * </div>
 * <ng-template #otherTemplate>
 *   Feature is disabled!
 * </ng-template>
 * ```
 *
 * @export
 * @class FeatureFlagDirective
 * @implements {OnInit}
 */
@Directive({ selector: '[opFF]' })
export class FeatureFlagDirective implements OnInit, OnDestroy {
	templateRef = inject(TemplateRef);
	viewContainer = inject(ViewContainerRef);
	featureFlagService = inject(FeatureFlagService);

	private hasView = false;
	private elseTemplateRef: TemplateRef<any> | null = null;
	private subscription = new Subscription();

	private show = new BehaviorSubject<Observable<boolean | undefined>>(of(undefined));

	// NOTE: Synchronous code has issues with the library not being ready in cypress tests.
	@Input() set opFF(feature: FeatureFlagKeys) {
		if (feature) {
			this.show.next(this.featureFlagService.selectFlag(feature));
		}
	}

	@Input() set opFFElse(elseTemplateRef: TemplateRef<any>) {
		this.elseTemplateRef = elseTemplateRef;
	}

	ngOnInit(): void {
		const sub = this.show.pipe(mergeMap((s) => s)).subscribe({
			next: (showTemplate) => {
				if (showTemplate && !this.hasView) {
					this.addTemplate();
				} else if (!showTemplate) {
					this.clearTemplate();
				}
			}
		});
		this.subscription.add(sub);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private addTemplate() {
		this.viewContainer.clear();
		this.viewContainer.createEmbeddedView(this.templateRef);
		this.hasView = true;
	}

	private clearTemplate() {
		this.viewContainer.clear();
		if (this.elseTemplateRef) {
			this.viewContainer.createEmbeddedView(this.elseTemplateRef);
		}
		this.hasView = false;
	}
}

/**
 * Directive to conditionally display a template based on a feature flag being false.
 *
 * This directive uses the `FeatureFlagService` to determine whether a specific feature
 * flag is enabled or disabled. It supports an optional "else" template to display
 * when the feature flag is enabled.
 *
 * Usage:
 * - Apply the directive to an element with the `opFFNot` input to specify the feature flag key.
 * - Optionally, use the `opFFNotElse` input to provide an alternate template to display when the feature flag is enabled.
 *
 * Example:
 * ```html
 * <div *opFFNot="'featureKey'; else otherTemplate">
 *   Feature is enabled!
 * </div>
 * <ng-template #otherTemplate>
 *   Feature is disabled!
 * </ng-template>
 * ```
 *
 * @export
 * @class FeatureFlagDirective
 * @implements {OnInit}
 */
@Directive({ selector: '[opFFNot]' })
export class FeatureFlagNotDirective implements OnInit, OnDestroy {
	templateRef = inject(TemplateRef);
	viewContainer = inject(ViewContainerRef);
	featureFlagService = inject(FeatureFlagService);

	private hasView = false;
	private elseTemplateRef: TemplateRef<any> | null = null;
	private subscription = new Subscription();

	private show = new BehaviorSubject<Observable<boolean | undefined>>(of(undefined));

	// NOTE: Synchronous code has issues with the library not being ready in cypress tests.
	@Input() set opFFNot(feature: FeatureFlagKeys) {
		if (feature) {
			this.show.next(this.featureFlagService.selectFlag(feature).pipe(map((flag) => !Boolean(flag))));
		}
	}

	@Input() set opFFNotElse(elseTemplateRef: TemplateRef<any>) {
		this.elseTemplateRef = elseTemplateRef;
	}

	ngOnInit(): void {
		const sub = this.show.pipe(mergeMap((s) => s)).subscribe({
			next: (showTemplate) => {
				if (showTemplate && !this.hasView) {
					this.addTemplate();
				} else if (!showTemplate) {
					this.clearTemplate();
				}
			}
		});
		this.subscription.add(sub);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private addTemplate() {
		this.viewContainer.clear();
		this.viewContainer.createEmbeddedView(this.templateRef);
		this.hasView = true;
	}

	private clearTemplate() {
		this.viewContainer.clear();
		if (this.elseTemplateRef) {
			this.viewContainer.createEmbeddedView(this.elseTemplateRef);
		}
		this.hasView = false;
	}
}
